import { useDispatch, useSelector } from 'react-redux';
import {
  addNewWebsiteStart,
  deleteWebsite,
  setSelectedWorkspace,
  toggleWebsiteStatusStart,
} from 'redux/websites/websites.actions';
import { useLocation, matchPath, useHistory } from 'react-router-dom';
import { setOnEditFAQ } from 'redux/faq/faq.actions';
import { INITIAL_ON_EDIT_FAQ } from 'components/FAQ/AddFAQ/constants';
import styles from './styles.module.scss';
import useTranslation from 'hooks/useTranslation';
import {
  BroomIconComponent,
  ChevronDownIcon,
  CloseModal,
  CloseOutlinedCompIcon,
  DefaultWorkspaceIcon,
  DefaultWorkspaceIconWhite,
  DeleteWebsiteIconComponent,
  SearchIconCompIcon,
} from 'assets/icons';
import { useState } from 'react';
import { useForm, useModal } from 'hooks';
import validateAddWebsite from 'components/WebsiteListMain/AddWebsiteModal/validateAddWebsite';
import Modal from 'components/Modal';
import ModalHeader from 'components/Modal/ModalHeader';
import ModalBody from 'components/Modal/ModalBody';
import ModalAddWebsiteForm from 'components/WebsiteListMain/AddWebsiteModal/ModalAddWebsiteForm';
import ModalFooter from 'components/Modal/ModalFooter';
import ModalAddWebsiteFooter from 'components/WebsiteListMain/AddWebsiteModal/ModalAddWebsiteFooter';
import { WorkspaceSwitched } from 'assets/sounds';
import Switch from 'components/Switch';
import clsx from 'clsx';

const DELETE_MODAL_INIT_V = {
  show: false,
  channelId: null,
};

const getWebsites = ({ websitesData }) => websitesData.websites.data;
const getLoadingWebsites = ({ websitesData }) => websitesData.websites.loading;
const getSelectedWorkspace = ({ websitesData }) =>
  websitesData.selectedWorkspace;
const getIsSideMenuCollapsed = ({ layoutData }) =>
  layoutData.isSideMenuCollapsed;
const getWebsitesData = ({ websitesData }) => websitesData.data;

function SelectWorkspace() {
  const isSideMenuCollapsed = useSelector(getIsSideMenuCollapsed);
  const [showMore, setShowMore] = useState(false);
  const [hasOperatorsByDefault, setHasOperatorsByDefault] = useState(true);
  const [visible, toggle] = useModal();
  const [allWebsitesModalVisible, toggleAllWebsitesModal] = useModal();
  const [showLoading, setShowLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('active');
  const [deleteModal, setDeleteModal] = useState(DELETE_MODAL_INIT_V);
  const [searchText, setSearchText] = useState('');

  const allWebsites = useSelector(getWebsites);
  const loadingWebsites = useSelector(getLoadingWebsites);
  const selectedWorkspace = useSelector(getSelectedWorkspace);
  const websitesData = useSelector(getWebsitesData);

  const { getTranslation } = useTranslation(['WEBSITES']);
  const { t } = getTranslation();

  const WEBSITES_CATEGORY = [
    {
      name: t('ACTIVE'),
      key: 'active',
    },
    {
      name: t('INACTIVE'),
      key: 'inactive',
    },
  ];

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const handleAddNewWebsite = (values) => {
    const website = {
      url: values.websiteUrl,
      title: values.title,
      hasOperatorsByDefault,
      handleToggle,
    };
    dispatch(addNewWebsiteStart(website));
  };

  const { values, errors, handleChange, handleSubmit, resetForm } = useForm(
    validateAddWebsite,
    handleAddNewWebsite,
    { websiteUrl: '', title: '' }
  );

  const otherWorkspacesStyles = {
    height: !showMore
      ? 0
      : isSideMenuCollapsed
      ? `${
          8 + // pt of other_workspaces
          0 + // height of categories_wrapper
          allWebsites
            ?.filter((w) =>
              selectedCategory === 'active' ? w.isActive : !w.isActive
            )
            ?.slice(0, 5)?.length *
            43 + // height of each ws item
          (allWebsites
            ?.filter((w) =>
              selectedCategory === 'active' ? w.isActive : !w.isActive
            )
            ?.slice(0, 5)?.length -
            1) *
            8 + // gap between each ws item
          // 25 +
          36 + // height of addNewWs Btn
          16 // pb of other_workspaces
        }px`
      : `${
          8 + // pt of other_workspaces
          25 + // height of categories_wrapper
          allWebsites
            ?.filter((w) =>
              selectedCategory === 'active' ? w.isActive : !w.isActive
            )
            ?.slice(0, 5)?.length *
            31 + // height of each ws item
          (allWebsites
            ?.filter((w) =>
              selectedCategory === 'active' ? w.isActive : !w.isActive
            )
            ?.slice(0, 5)?.length +
            1) *
            8 + // gap between each ws item
          25 +
          24 + // height of addNewWs Btn
          16 // pb of other_workspaces
        }px`,
  };

  const handleToggle = (event) => {
    if (event) event.preventDefault();
    toggle();
    resetForm();
    setHasOperatorsByDefault(true);
  };

  const handleWsChange = (website) => {
    setShowLoading(true);
    setShowMore(false);
    dispatch(setSelectedWorkspace(website));

    const editingFAQPage = matchPath(location.pathname, {
      path: '/faq/:channelId/:faqId',
      exact: true,
      strict: true,
    });
    const createNewFAQPage = matchPath(location.pathname, {
      path: '/faq/:channelId',
      exact: true,
      strict: true,
    });

    if (editingFAQPage?.isExact || createNewFAQPage?.isExact) {
      const channelId = website.id;
      history.replace(`/faq/${channelId}`);
      dispatch(setOnEditFAQ({ ...INITIAL_ON_EDIT_FAQ, channelId }));
    }

    setTimeout(() => {
      setShowLoading(false);
      const audio = new Audio(WorkspaceSwitched);
      audio.play();
    }, 700);
  };

  const handleToggleWebsiteStatus = (isChecked, websiteId) => {
    dispatch(
      toggleWebsiteStatusStart({
        id: websiteId,
        isActive: isChecked,
      })
    );
  };

  const handleDeleteWebsite = () => {
    setDeleteModal(DELETE_MODAL_INIT_V);
    dispatch(deleteWebsite(deleteModal.channelId));
  };

  const renderLoading = (
    <div className={styles.center} data-collapse={isSideMenuCollapsed}>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
      <div className={styles.wave} data-collapse={isSideMenuCollapsed}></div>
    </div>
  );

  const renderDeleteWebsite = (channelId) => (
    <div
      className={clsx(styles.action_btn)}
      onClick={() => setDeleteModal({ show: true, channelId })}
    >
      <DeleteWebsiteIconComponent />
      {t('DELETE_WEBSITE')}
    </div>
  );

  const renderDisableDeleteWebsite = (channelId) => (
    <div className={clsx('relative', styles.disbaled_delete)}>
      <div
        className={clsx(styles.action_btn, styles.action_btn_disabled)}
        // onClick={() => setDeleteModal({ show: true, channelId })}
      >
        <DeleteWebsiteIconComponent />
        {t('DELETE_WEBSITE')}
      </div>

      <div className={styles.disabled_tooltip}>
        {t('DISABLE_DELETE_TOOLTIP')}
        <span
          onClick={() => {
            toggleAllWebsitesModal();
            history.push('/payment/package-upgrading');
          }}
        >
          {t('BUY_PACKAGE')}
        </span>
      </div>
    </div>
  );

  const handleRenderDeleteButton = ({ website_url, channelId }) => {
    const isCreator = localStorage.getItem('isCreatorAdmin') === 'true';
    const hasDeleteChannelPermission = websitesData?.raypackage?.deleteChannel;
    const isDifferentSite = websitesData?.container?.site !== website_url;

    if (isDifferentSite) {
      if (!hasDeleteChannelPermission) {
        return renderDisableDeleteWebsite(channelId);
      } else if (hasDeleteChannelPermission && isCreator) {
        return renderDeleteWebsite(channelId);
      }
    }

    return null;
  };

  return (
    <>
      <div
        className={styles.workspace_wrapper}
        data-show={showMore}
        data-collapse={isSideMenuCollapsed}
      >
        {loadingWebsites || showLoading ? (
          renderLoading
        ) : (
          <>
            <div
              className={styles.selected_workspace}
              onClick={() => {
                if (!loadingWebsites) {
                  setShowMore((prev) => !prev);
                }
              }}
            >
              <div className={styles.right_part}>
                <div
                  className={styles.workspace_logo_container}
                  data-collapse={isSideMenuCollapsed}
                >
                  {!!loadingWebsites ? null : ( // <Skeleton width={38} height={38} borderRadius={8} />
                    <>
                      <img
                        src={selectedWorkspace?.logoUrl}
                        alt='logo'
                        className={styles.workspace_logo}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = DefaultWorkspaceIcon;
                        }}
                      />

                      <div
                        data-show={showMore}
                        className={styles.more_workspaces_expander}
                        data-collapse={isSideMenuCollapsed}
                      >
                        <ChevronDownIcon />
                      </div>
                    </>
                  )}
                </div>

                <div
                  className={styles.website_stuff}
                  data-collapse={isSideMenuCollapsed}
                >
                  <span className={styles.website_word} data-show={showMore}>
                    {!!loadingWebsites
                      ? // <Skeleton width={80} height={18} borderRadius={2} />
                        null
                      : t('WEBSITE_WORD')}
                  </span>
                  <span
                    className={styles.website_name}
                    title={selectedWorkspace?.title}
                    data-show={showMore}
                  >
                    {!!loadingWebsites
                      ? // <Skeleton width={80} height={18} borderRadius={2} />
                        null
                      : selectedWorkspace?.title}
                  </span>
                </div>
              </div>

              {!loadingWebsites && (
                <div
                  data-show={showMore}
                  className={styles.left_part}
                  data-collapse={isSideMenuCollapsed}
                >
                  <ChevronDownIcon />
                </div>
              )}
            </div>

            <div
              data-show={showMore}
              style={otherWorkspacesStyles}
              className={styles.other_workspaces}
            >
              <div
                className={styles.categories_wrapper}
                data-collapse={isSideMenuCollapsed}
              >
                {WEBSITES_CATEGORY.map((c, idx) => {
                  return (
                    <div
                      key={c.key}
                      data-selected={selectedCategory === c.key}
                      data-statuskey={c.key}
                      onClick={() => {
                        setSelectedCategory(c.key);
                      }}
                      className={styles.category_item}
                    >
                      {c.name}
                    </div>
                  );
                })}
              </div>
              {allWebsites
                ?.filter((ws) =>
                  selectedCategory === 'active' ? ws.isActive : !ws.isActive
                )
                ?.slice(0, 5)
                ?.map((ws) => {
                  return (
                    <div key={ws.id} className={styles.other_ws}>
                      <div
                        className={styles.other_workspace_logo_container}
                        data-collapse={isSideMenuCollapsed}
                        onClick={() => handleWsChange(ws)}
                      >
                        <img
                          src={ws?.logoUrl}
                          alt='logo'
                          className={styles.other_workspace_logo}
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null; // prevents looping
                            currentTarget.src = DefaultWorkspaceIcon;
                          }}
                        />
                      </div>

                      <div className={styles.website_stuff_wrapper}>
                        <div
                          className={styles.website_stuff}
                          onClick={() => handleWsChange(ws)}
                          data-collapse={isSideMenuCollapsed}
                        >
                          <span
                            data-show={true}
                            className={styles.other_website_name}
                            title={ws?.title}
                          >
                            {ws?.title}
                          </span>

                          {/* <span
                            data-status={ws.isActive ? 'active' : 'inactive'}
                            className={styles.status}
                          >
                            {ws.isActive ? t('ACTIVE') : t('INACTIVE')}
                          </span> */}
                        </div>

                        <div
                          className={styles['ws-switch']}
                          data-collapse={isSideMenuCollapsed}
                        >
                          {/* <MenuSettingsIconComponent /> */}
                        </div>
                      </div>
                    </div>
                  );
                })}

              <div
                className={styles.show_all_websites}
                data-collapse={isSideMenuCollapsed}
                onClick={() => {
                  toggleAllWebsitesModal();
                }}
              >
                <span>{t('SEE_ALL_WEBSITES')}</span>
                {/* <ArrowCircleIconComponent /> */}
              </div>

              <div
                className={styles.add_new_ws}
                data-collapse={isSideMenuCollapsed}
                onClick={handleToggle}
              >
                <div className={styles.plus}>+</div>

                <span
                  className={styles.text}
                  data-collapse={isSideMenuCollapsed}
                >
                  {t('ADD_WEBSITE')}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal visible={allWebsitesModalVisible}>
        <ModalHeader>
          <div className={styles.all_websites_modal_header}>
            <h4 className={styles.all_websites_modal_header_title}>
              لیست همه وب سایت ها
            </h4>

            <div className={styles.close_search}>
              <div className={styles.search_wrapper}>
                <input
                  placeholder='جستجو در بین وب سایت ها'
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />

                <SearchIconCompIcon />
              </div>

              <div
                className={styles.close_wrapper}
                onClick={() => toggleAllWebsitesModal()}
              >
                <CloseOutlinedCompIcon />
              </div>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className={styles.all_websites_listing_wrapper}>
            {allWebsites
              ?.filter(
                (w) =>
                  w?.title?.includes(searchText) || w?.url?.includes(searchText)
              )
              ?.map((website) => {
                const { title, url, id, isActive } = website || {};
                const activeStatus = isActive ? 'active' : 'inactive';

                return (
                  <div key={id} className={styles.website_item}>
                    <div className={styles.website_info}>
                      <div className={styles.title_and_logo}>
                        <div className={styles.logo_wrapper}>
                          <img
                            src={website?.logoUrl}
                            alt='logo'
                            className={styles.logo}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src = DefaultWorkspaceIconWhite;
                            }}
                          />
                        </div>

                        <div className={styles.website_title_address}>
                          <a
                            href={url}
                            className={styles.website_title}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {title}
                          </a>
                        </div>
                      </div>

                      <div className={styles.switch_and_url}>
                        <div
                          className={styles.website_status_wrapper}
                          data-status={activeStatus}
                        >
                          <span
                            className={styles.website_status}
                            data-status={activeStatus}
                          >
                            {isActive ? t('ACTIVE') : t('INACTIVE')}
                          </span>

                          <Switch
                            checked={isActive}
                            onChecked={(s) => {
                              handleToggleWebsiteStatus(s, id);
                            }}
                          />
                        </div>
                        <a
                          href={url}
                          className={styles.website_address}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {url}
                        </a>
                      </div>
                    </div>

                    <div className={styles.actions_wrapper}>
                      {handleRenderDeleteButton({
                        website_url: url,
                        channelId: id,
                      })}

                      <div
                        onClick={() => {
                          handleWsChange(website);
                          toggleAllWebsitesModal();
                          history.replace(`/settings/widget#customization`);
                        }}
                        className={clsx(styles.action_btn)}
                      >
                        <BroomIconComponent />
                        {t('CUSTOMIZATION')}
                      </div>

                      <div
                        className={clsx(
                          styles.action_btn,
                          styles.primary_action_btn
                        )}
                        onClick={() => {
                          handleWsChange(website);
                          toggleAllWebsitesModal();
                        }}
                      >
                        {t('CHOOSE_WEBSITE')}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </ModalBody>
      </Modal>

      <Modal visible={deleteModal.show}>
        <ModalHeader>
          <div className={styles.delete_modal_header}>
            <h2>{t('DELETE_TITLE')}</h2>

            <button
              onClick={() => setDeleteModal(DELETE_MODAL_INIT_V)}
              className={styles.modal_close}
            >
              <img src={CloseModal} alt='Close Icon' />
            </button>
          </div>
        </ModalHeader>

        <ModalBody>
          <h1 className={styles.delete_modal_body}>{t('DELETE_DESC')}</h1>
        </ModalBody>

        <ModalFooter>
          <div className={styles.delete_modal_footer}>
            <button
              className={clsx(styles.delete_app_btn, 'primary')}
              onClick={handleDeleteWebsite}
            >
              {t('DELETE_CONFIRMATION_BTN')}
            </button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal visible={visible}>
        <ModalHeader title={t('MODAL').ADD_WEBSITE_TITLE} />
        <ModalBody>
          <div className='modal__container__body__addwebsite'>
            <ModalAddWebsiteForm
              values={values}
              errors={errors}
              handleAddNewWebsite={handleSubmit}
              handleChange={handleChange}
              hasOperatorsByDefault={hasOperatorsByDefault}
              setHasOperatorsByDefault={setHasOperatorsByDefault}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalAddWebsiteFooter
            websiteUrl={values.websiteUrl}
            handleAddNewWebsite={handleSubmit}
            toggle={handleToggle}
          />
        </ModalFooter>
      </Modal>
    </>
  );
}

export default SelectWorkspace;
